import axios from 'axios';
import { useEffect, useState } from 'react';

export interface IUseImage {
	isLoaded: boolean;
	isError: boolean;
	handleError: (error: boolean) => void;
	handleLoad: (load: boolean) => void;
}

export const useImage = (): IUseImage => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [isError, setIsError] = useState(false);

	const handleLoad = (load: boolean) => {
		setIsLoaded(load);
	};

	const handleError = (error: boolean) => {
		setIsError(error);
	};

	return {
		isLoaded,
		isError,
		handleError,
		handleLoad,
	};
};

// Function to get image dimensions
const getImageSize = async (url: string): Promise<IImgSize> =>
	await new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			resolve({ width: img.width, height: img.height });
		};
		img.onerror = reject;
		img.src = url;
	});

export interface IImgSize {
	width: number;
	height: number;
}

// Function to get image file size
const getFileSize = async (url: string): Promise<number | null> => {
	try {
		const response = await axios.head(url);
		const contentLength = response.headers['content-length'];
		return contentLength ? parseInt(contentLength, 10) : null;
	} catch (error) {
		console.error('Error getting file size:', error);
		return null;
	}
};

export const useImageSize = (imageUrl: string): IImgSize & { size: number | null } => {
	const [imageProps, setImageProps] = useState<IImgSize & { size: number | null }>({ width: 0, height: 0, size: 0 });

	useEffect(() => {
		getImageSize(imageUrl)
			.then((size) => {
				setImageProps((prev) => ({
					...prev,
					width: size.width,
					height: size.height,
				}));
			})
			.catch((error) => {
				console.error('Error loading image:', error);
			});

		getFileSize(imageUrl)
			.then((size) => {
				setImageProps((prev) => ({
					...prev,
					size,
				}));
			})
			.catch((error) => {
				console.error('Error getting file size:', error);
			});
	}, [imageUrl]);

	return imageProps;
};
